// One function per detail-modal endpoint (N1-N16 + X5), story 123 / MIC-4252.
//
// This is the ONLY place in `configuration/orders/detail/` allowed to compare against `null`/
// `undefined` explicitly (CLAUDE.md null-avoidance rule, frontier exception) — see
// plan-jupiter.md §1.4 "Ausencias de frontera -> estados 'sin dato'" for the authoritative table
// this mapping implements. Everything past this file reads business defaults ('', {}, [], false),
// never `null`.
import { apiGet, apiPost, apiPut } from '../../../generic/Api_Functions'
import { operationStatusFromWire } from './operationStatus'
import { PART_LIFECYCLE_GROUPS } from './partLifecycleGroup'

const asObject = (value) => value ?? {}

const asEmptyString = (value) => (value === null || value === undefined ? '' : value)

// The sheet fields that Sun serves in N1 since sun#3999/#4004 (createdNode, extendedColumn1..12, workflow,
// forecast, expirationDate). An older Sun does not send them: the detail then reads X2 (`fetchSheet`).
const EXTENDED_COLUMN_COUNT = 12
const hasSheetFields = (data) => data.extendedColumn1 !== undefined

// N1 sheet fields -> the SAME shape `mapSheetDto` gives for X2. X2 names the extended columns
// `extended_column1..12` (the names the General block and the extended-column catalog use), N1 names
// them `extendedColumn1..12`. `workflow: null` is an order without workflow, as in X2.
const sheetOfHeaderDto = (data) => {
  const extendedColumns = {}
  for (let column = 1; column <= EXTENDED_COLUMN_COUNT; column += 1) {
    extendedColumns[`extended_column${column}`] = data[`extendedColumn${column}`]
  }
  return {
    createdNode: asObject(data.createdNode),
    ...extendedColumns,
    forecast: data.forecast,
    expirationDate: data.expirationDate,
    workflow: { ...asObject(data.workflow), used: !!(data.workflow && data.workflow.used) },
    withoutWorkflow: data.workflow === null
  }
}

// FoDetailHeaderDto (N1) -> business shape consumed by DetailHeader/GeneralBlock.
export const mapHeaderDto = (data) => ({
  fabricationOrderId: data.fabricationOrderId,
  // Story 213 v2 (C1c): only present with the vision on; absent means "not sensitive", never a reason for a boundary
  sensitive: data.sensitive === true,
  code: data.code,
  partReference: asObject(data.partReference),
  mainImageDocumentUuid: asEmptyString(data.mainImageDocumentUuid),
  client: asObject(data.client),
  order: asObject(data.order),
  lineOrder: asObject(data.lineOrder),
  parentFabricationOrder: asObject(data.parentFabricationOrder),
  status: data.status,
  pauseState: data.pauseState,
  // RN19.1: the consumer only reads these when pauseState === 'PARTIALLY_PAUSED', where Sun
  // guarantees them present — no null-avoidance mapping needed here (plan §1.4).
  pausedPartCount: data.pausedPartCount,
  activePartCount: data.activePartCount,
  offersPause: data.offersPause,
  offersResume: data.offersResume,
  activeOperationCount: data.activeOperationCount,
  // ⚠️⚠️ S409 — HERMANO de `activeOperationCount`, NO anidado dentro de `currentOperation`, y el
  // motivo invalida el enunciado con el que lo pedí: `currentOperation` SOLO viaja cuando hay
  // EXACTAMENTE UNA operación en curso (S281). Anidada, la fecha faltaría justo en el caso de
  // VARIAS — que es una de las dos frases que tiene que componer.
  //
  // Es el primer momento en que alguien quedó registrado en la operación
  // (`device_worker_fabrication_order.start_date`), no la fecha planificada: usar la del plan
  // diría "lleva 83 días en curso" contando desde un día en el que quizá no arrancó.
  //
  // ⚠️ AUSENTE NO ES «NINGUNA EN CURSO». Hay operaciones en curso en las que nadie quedó
  // registrado (1 de las 31 de la base), así que este campo y `activeOperationCount` son
  // INDEPENDIENTES: puede haber conteo sin fecha, y ninguno se infiere del otro.
  currentOperationStartedAt: asEmptyString(data.currentOperationStartedAt),
  currentOperation: asObject(data.currentOperation),
  // R-20/C1: statusProgress is a text enum (UNKNOWN|IN_PROGRESS|ACTIVE_EXECUTION|FINISHED|
  // CANCELED|PARTIALLY_FINISHED — the last one since story 178: CLOSED with the partial-close mark).
  // It is rendered verbatim (translated) and NEVER converted to a number.
  statusProgress: asEmptyString(data.statusProgress),
  // C1: null means "not computable" for progressLabel.js — never rendered as a bar nor as 0%.
  progressPercent: asEmptyString(data.progressPercent),
  quantity: {
    nominal: asEmptyString(data.quantity ? data.quantity.nominal : null),
    good: asEmptyString(data.quantity ? data.quantity.good : null),
    rejected: asEmptyString(data.quantity ? data.quantity.rejected : null),
    pending: asEmptyString(data.quantity ? data.quantity.pending : null),
    // S412 — piezas CANCELADAS. ⚠️⚠️ NO ES UN SUMANDO DE `good + pending = nominal`, y Sun lo deja
    // escrito en el DTO: "informativo, puede exceder `nominal` legítimamente". No es una rareza
    // teórica — el contador es HISTÓRICO y las cancelaciones se acumulan, así que una orden a la
    // que le han bajado la cantidad dos veces enseña 20 canceladas con un nominal de 10. Quien lo
    // trate como un término de la suma sacará una cuenta absurda.
    cancelled: asEmptyString(data.quantity ? data.quantity.cancelled : null)
  },
  totalQuantity: asEmptyString(data.totalQuantity),
  deliveryDate: asEmptyString(data.deliveryDate),
  targetDate: asEmptyString(data.targetDate),
  // ⚠️⚠️ S413 — LLEGABA Y NO SE LEÍA. Sun lo sirve (`FoDetailHeaderDto.java:61`) y su única
  // aparición en el cliente era un comentario diciendo que hacía falta pedirlo. Un mapeador que
  // enumera campos a mano DESCARTA EN SILENCIO todo lo que el servidor añada: es la quinta vez que
  // pasa en esta ficha (addendum 55, `lastReport`, `inspectionItems`, los dos de S419 y éste), y
  // por eso hay ahora un test de contrato que compara las claves servidas con las mapeadas.
  minStartDate: asEmptyString(data.minStartDate),
  plannedEndDate: asEmptyString(data.plannedEndDate),
  priority: asEmptyString(data.priority),
  planType: asObject(data.planType),
  deviation: {
    // deviationLabel.js decides the "not computable" literal from `days` itself — kept as the
    // raw frontier value (possibly null) on purpose, per plan §1.4.
    days: data.deviation ? data.deviation.days : null,
    basis: data.deviation ? data.deviation.basis : 'NOT_COMPUTABLE',
    basisDate: asEmptyString(data.deviation ? data.deviation.basisDate : null)
  },
  lateAgainstTargetDate: data.lateAgainstTargetDate,
  lastModified: data.lastModified,
  lastModifiedBy: asObject(data.lastModifiedBy),
  qrAvailable: data.qrAvailable,
  // Only with a Sun that serves the sheet fields in N1: `sheet` is then what X2 would give (same
  // shape as `mapSheetDto`) and the detail needs no X2 read on open. Absent with an older Sun.
  ...(hasSheetFields(data) ? { sheet: sheetOfHeaderDto(data) } : {})
  // ⚠ Fix (pasada 11): an older N1 (FoDetailHeaderDto) never sent `workflow`, and this DTO must not
  // fabricate a `workflow: { used }` field for it. `workflow.used` (S142) then comes from X2 (see
  // mapSheetDto/fetchSheet below).
})

export const fetchHeader = (id) =>
  apiGet(`fabrication-orders/${id}/detail/header`).then(mapHeaderDto)

// FabricationOrderDto (X2, `03-dominio.md` §C.1: "sin cambios · Ficha General (se reutiliza tal
// cual)") -> la "ficha" de la que GeneralBlock LEE sus campos no-N1 (`createdNode`, los DOCE campos
// PLANOS `extended_column1..12`, `workflow`).
//
// ⚠ Desde N16 (addendum 9) esta fila **ya no se reenvía al guardar**: el guardado manda cuerpo
// parcial y la preservación del resto es del endpoint. X2 queda como lectura de PANTALLA —por eso
// sigue aquí— y el mapper no recorta nada: lo que Sun envía viaja entero para lo que la ficha
// necesite mostrar.
export const mapSheetDto = (data) => {
  const row = asObject(data)
  return {
    ...row,
    createdNode: asObject(row.createdNode),
    // ⚠ Fix (verificación del hub, 2026-09-14): este mapper reducía `workflow` a `{ used }` y
    // tiraba el resto — incluido `name`, que el bloque necesita mostrar, y que además viajaba en la
    // objeto que la ficha necesita para mostrar el nombre del workflow. Se conserva entero y solo se
    // normaliza `used`.
    workflow: { ...asObject(row.workflow), used: !!(row.workflow && row.workflow.used) },
    // H179 (RN5, C5): una OF SIN workflow llega con `workflow: null` EXPLÍCITO. Se decide aquí, antes de
    // que `asObject` lo funda con un objeto vacío: una fila que todavía no ha llegado (`{}`) no es una OF
    // sin workflow, y confundirlas escondería «Open workflow» a quien sí lo tiene.
    withoutWorkflow: Object.prototype.hasOwnProperty.call(row, 'workflow') && row.workflow === null
  }
}

// ⚠ Fix (verificación del hub, 2026-09-14): X2 busca POR CÓDIGO, no por id. Sun lo declara sin
// ambigüedad (`FabricationOrderRest.java:734-745`: `@RequestParam("fabricationOrder") String`,
// `findAllByFabricationOrderIn(singletonList(fabricationOrder))`, y `ResponseEntity.ok(null)` si no
// encuentra nada). Se le venía pasando el id numérico, así que la respuesta era 200 con cuerpo
// VACÍO para toda orden real: `workflow.used` siempre falso (S142), sin columnas extendidas, y —lo
// grave— la fila completa que GeneralBlock reenvía al guardar llegaba vacía.
export const fetchSheet = (fabricationOrderCode) =>
  apiGet(`fabricationOrder?fabricationOrder=${encodeURIComponent(fabricationOrderCode)}`).then(mapSheetDto)

// FoDetailAlertsDto (N2) -> business shape consumed by the block navigation label.
export const mapAlertsDto = (data) => ({
  materials: !!(data && data.materials),
  materialsReason: asEmptyString(data ? data.materialsReason : null)
})

export const fetchAlerts = (id) =>
  apiGet(`fabrication-orders/${id}/detail/alerts`).then(mapAlertsDto)

// Un operario de una OPERACIÓN. Vive aquí arriba porque lo usan DOS mapeos —la fila de N3 y el
// paso de pieza de N13— y tiene que ser el mismo: es el mismo dato, de la misma fuente y con el
// mismo orden. Dos copias acabarían divergiendo.
const mapOperationWorkerDto = (worker) => ({
  workerId: worker.workerId,
  workerCode: asEmptyString(worker.workerCode),
  workerName: asEmptyString(worker.workerName)
})

// FoDetailOperationDto (N3, list) -> business shape consumed by OperationsBlock (RN33/RN34, T3).
export const mapOperationDto = (data) => ({
  jobReferenceId: data.jobReferenceId,
  // ADDENDUM 17 (E23): `jobOrder` se queda aunque nadie lo pinte —está a `null` en las 2.907 filas de
  // este volcado, pero es un campo real que otra instalación puede poblar, y retirarlo porque ESTA
  // base lo tiene vacío sería decidir sobre el modelo a partir de un volcado.
  jobOrder: asEmptyString(data.jobOrder),
  // ⚠️ `code` es el valor que hasta ahora llegaba bajo el nombre `name`: mismo valor, campo
  // renombrado. Y `name` es un campo NUEVO, casi siempre nulo. Seguir leyendo `name` sin darse cuenta
  // habría dejado la columna vacía en 2.905 de 2.907 operaciones SIN QUE NADA FALLARA, que es el
  // modo en que este cambio muerde.
  code: asEmptyString(data.code),
  name: asEmptyString(data.name),
  technology: asObject(data.technology),
  // S380: el cable trae "NOT AVAILABLE" CON ESPACIO para ese estado (paridad con MOS, decisión del
  // PO) y el nombre del enum para los otros siete. Se traduce aquí, que es donde viven el resto de
  // rarezas del cable, para que aguas abajo haya un solo vocabulario.
  status: operationStatusFromWire(data.status),
  quantityDone: asEmptyString(data.quantityDone),
  // ADDENDUM 11 (E17), ampliación: N3 y N10 no sirven "el mismo cálculo" sino literalmente el mismo
  // objeto —los dos servicios llaman a `jobQuantitiesReader.byJob(...)`—, así que el campo se
  // renombra en las dos puertas. `quantityPending` es lo que QUEDA por reportar, no un objetivo.
  quantityPending: asEmptyString(data.quantityPending),
  plannedStartDate: asEmptyString(data.plannedStartDate),
  plannedEndDate: asEmptyString(data.plannedEndDate),
  device: asObject(data.device),
  planningGroup: asEmptyString(data.planningGroup),
  planningGroupOrigin: asEmptyString(data.planningGroupOrigin),
  // The job's position on the balanced line (TYPE: its own; LINE: its preferred device's). Empty when
  // the workflow is not balanced or the job was not placed. Key of the "balanced order" view.
  linePosition: asEmptyString(data.linePosition),
  // (S461: `planningGroupEditable` y `planningGroupNotEditableReason` ya NO se leen. El grupo se edita en
  // cualquier estado —RN40 y V-10 derogados—; solo decide el permiso. Sun los retira de N3.)
  remark: asEmptyString(data.remark),
  // ⚠️⚠️ LOS SEIS DE LA PESTAÑA WORKFLOW. Hasta ahora SOLO estaban en N4, o sea a una petición por
  // operación. Con mediana 4 operaciones por OF, p90 14 y un peor caso medido de 70, pintarlos en
  // la lista por esa vía eran hasta 70 peticiones al abrir la pestaña. Suben a N3.
  //
  // 📌 No son datos nuevos: son los MISMOS que ya calcula N4, servidos también en la lista. Sun
  // reutiliza sus lecturas por OF para cinco de los seis; el par de rechazos necesitó una lectura
  // batcheada nueva porque iba a 3 consultas POR OPERACIÓN.
  //
  // ⚠️ AUSENTES, NUNCA A CERO, y por eso `asEmptyString` y no `|| 0`: una operación sin rechazos y
  // una cuyo dato no se ha calculado son dos hechos distintos y la tabla los pinta distinto (RN8).
  // Un `|| 0` aquí convertiría el segundo en el primero y nadie lo vería.
  rejectedQuantity: asEmptyString(data.rejectedQuantity),
  // Viaja CON la cantidad, no es un adorno: "2 (SCRAP)" y "2 (REWORK)" no son el mismo hecho.
  // ⚠️ TIENE TRES VALORES, no dos: `UNKNOWN` existe cuando la pieza está rechazada pero no consta
  // si se desechó o se reprocesó. No es un hueco — es un valor real y se pinta como los otros dos.
  rejectedDisposition: asEmptyString(data.rejectedDisposition),
  openIncidenceCount: asEmptyString(data.openIncidenceCount),
  theoreticalDurationSeconds: asEmptyString(data.theoreticalDurationSeconds),
  actualDurationSeconds: asEmptyString(data.actualDurationSeconds),
  // S443 — el TOTAL TEÓRICO DE LA ORDEN y su desglose, servidos por Sun (`3c46143820`, 23/09):
  // `theoreticalOrderDurationSeconds = theoreticalDurationSeconds × nominalQuantity +
  // initialPreparationSeconds`. Se enseña tal cual: el front no multiplica ni suma nada.
  nominalQuantity: asEmptyString(data.nominalQuantity),
  theoreticalOrderDurationSeconds: asEmptyString(data.theoreticalOrderDurationSeconds),
  initialPreparationSeconds: asEmptyString(data.initialPreparationSeconds),
  // ⚠️⚠️ `null` en N3 dice «la operación NO GESTIONA preparación» (compuerta de S323), y es un
  // hecho, no un hueco. Un campo que NO LLEGA es otra cosa —el jar no es el esperado— y no se
  // puede leer como «no gestionada». Se decide aquí, antes de que `asEmptyString` los funda.
  initialPreparationManaged: data.initialPreparationSeconds !== null && data.initialPreparationSeconds !== undefined,
  initialPreparationServed: Object.prototype.hasOwnProperty.call(data, 'initialPreparationSeconds'),
  // S424 — el OPERARIO PLANIFICADO (`planification_task_worker`), forma cerrada por Sun el 23/09:
  // `plannedWorkers: [{workerId, code, name}]`, SIEMPRE PRESENTE, vacío = sin planificar.
  // ⚠️⚠️ «NO LLEGA» Y «LLEGA VACÍO» SON DOS HECHOS, y se decide AQUÍ, antes de que `|| []` los funda:
  // vacío dice «nadie planificado»; ausente dice que el servidor no es el que este cliente espera
  // (el caso del `external` de S341). Pintar «sin planificar» sobre un campo que no llega sería
  // inferir una ausencia de un silencio — y en la base del PO acertaría por casualidad (0 filas).
  // Se mapea a la forma de operario que ya escriben `operationWorkerLabels` (`workerCode`,
  // `workerName`), para que un nombre se escriba igual aquí que en el recorrido de una pieza.
  plannedWorkersServed: Array.isArray(data.plannedWorkers),
  plannedWorkers: (data.plannedWorkers || []).map((worker) => ({
    workerId: worker.workerId,
    workerCode: asEmptyString(worker.code),
    workerName: asEmptyString(worker.name)
  }))
  // (`operationWorkers` lo retiró Sun de N3 el 23/09, con la columna —D11—. El de N13, Seguimiento de
  // Pieza, es otro campo y se sigue leyendo abajo.)
})

// N3 — order in which Sun returns the list IS the process order (RN34): this mapper never sorts.
export const fetchOperations = (id) =>
  apiGet(`fabrication-orders/${id}/detail/operations`).then((data) => (data || []).map(mapOperationDto))

// FoDetailOperationExpansionDto (N4) -> business shape consumed by OperationsBlock's row expansion.
export const mapOperationExpansionDto = (data) => ({
  rejectedQuantity: asEmptyString(data.rejectedQuantity),
  rejectedDisposition: asEmptyString(data.rejectedDisposition),
  // ADDENDUM 18 (E24), CORREGIDO por decisión del PO (S341): la marca de job EXTERNO, la misma
  // casilla EXT que el editor de workflow enseña al entrar en un nodo.
  //
  // El addendum 18 hacía `!!data.external` y decía que no había tercera rama. El razonamiento era
  // correcto sobre la COLUMNA (0 nulos en 2.907 jobs) y equivocado sobre el CAMPO: Sun lo sirve
  // SIEMPRE PRESENTE, así que un campo que no llega no puede significar "no es externo" — solo puede
  // significar que el servidor al otro lado no es el que este cliente espera.
  //
  // No es una hipótesis. Se vio en pantalla el 15/09: el jar en marcha era de las 15:19 y el commit
  // de Sun de este addendum de las 15:45, así que N4 no traía el campo — y la ficha afirmaba
  // "EXTERNO: No" sobre el job 1913 de `ofref2p`, que en base de datos es `extern = t`. Un backend
  // desfasado no deja un hueco que alguien mire: deja un dato plausible y equivocado que nadie
  // cuestiona.
  //
  // Por eso la ausencia se conserva como ausencia (`''`, la misma forma que el resto del fichero) y
  // NO se colapsa a `false`. Ojo al usarla: `''` es falsy, así que quien la pinte tiene que mirar la
  // ausencia ANTES que el valor — un ternario sobre la verdad la volvería a convertir en "No".
  // Cualquier cosa que no sea un booleano de verdad entra por aquí también: tampoco es una respuesta.
  external: data.external === true || data.external === false ? data.external : '',
  // ADDENDUM 13 (E19) — TRES duraciones, con raíces que no se solapan. El nombre viejo del segundo,
  // `declaredTimeSeconds`, decía lo que el dato NO es: significa "tiempo real de los pasos cerrados"
  // y "declarado" invitaba a pensar que alguien lo declaró. Con dos duraciones más al lado, ese
  // nombre habría envejecido mal.
  theoreticalDurationSeconds: asEmptyString(data.theoreticalDurationSeconds),
  actualDurationSeconds: asEmptyString(data.actualDurationSeconds),
  scheduledDurationSeconds: asEmptyString(data.scheduledDurationSeconds),
  // ADDENDUM 16 (E22) parte A: los cuatro traen el valor almacenado AUNQUE SEA CERO, gestionada o no
  // la preparación. Es una excepción consciente a RN8, tomada por el PO con el coste dicho: 2.867 de
  // 2.907 jobs guardan un cero, y mostrar ausencia en vez del cero habría dicho "pendiente" en casi
  // todas las operaciones.
  initialPreparationSeconds: asEmptyString(data.initialPreparationSeconds),
  initialPreparationOperators: asEmptyString(data.initialPreparationOperators),
  intermediatePreparationSeconds: asEmptyString(data.intermediatePreparationSeconds),
  intermediatePreparationOperators: asEmptyString(data.intermediatePreparationOperators),
  // ⚠️ Regla CONTRARIA a la de arriba, y la diferencia es deliberada: aquí un cero no se puede
  // distinguir de un campo sin rellenar (131 de 2.907 jobs lo traen), así que la ausencia no se
  // afirma como "requiere cero operarios".
  requiredOperators: asEmptyString(data.requiredOperators),
  reportCount: asEmptyString(data.reportCount),
  // (`workers` ya no existe: el «desde cuándo» sale de `statusChanges` —ver `firstActiveSince`— y Sun
  // lo retiró de N4 el 23/09.)
  // ⚠️ Y `reporters` lleva la misma entidad con los mismos nombres desde el addendum 16 parte D: el
  // campo decía `userId` mientras el javadoc de su clase decía "worker". Servir la misma entidad con
  // dos nombres en la misma tarjeta es como sobrevive un defecto después de encontrado.
  reporters: data.reporters || [],
  // (`inspectionSummary` —el veredicto FAIL resumido— lo retiró Sun el 23/09 por decisión del PO.)
  lastUpdatedAt: asEmptyString(data.lastUpdatedAt),
  remark: asEmptyString(data.remark),
  openIncidenceCount: asEmptyString(data.openIncidenceCount),
  // ⚠️⚠️ S422 — `interactions` YA NO EXISTE; LO SUSTITUYE `statusChanges`, Y ES OTRA PREGUNTA.
  // `interactions` salía de `device_worker_fabrication_order` y decía «quién trabajó y cuánto».
  // `statusChanges` sale de `device_fabrication_order` y dice «quién cambió el estado y por qué».
  // Nombre nuevo a propósito, dice el addendum 60: con el viejo, alguien las fusionaría dentro de un
  // año. Y NO trae `duration` ni `incidence`, por decisión del PO.
  //
  // 📌 LA TABLA VIEJA NO HA DESAPARECIDO. Sigue alimentando el tiempo real de la operación y
  // Seguimiento de Pieza (el «desde cuándo» del titular ya sale de aquí, S432). Las dos coexisten, y eso no
  // es deuda mientras esté escrito: lo sería si alguien creyera que una sustituyó a la otra.
  statusChanges: (data.statusChanges || []).map((change) => ({
    id: change.id,
    changedAt: asEmptyString(change.changedAt),
    endedAt: asEmptyString(change.endedAt),
    // ⚠️⚠️ `endedAt: null` SIGNIFICA TRAMO ABIERTO, NO DATO AUSENTE. Se decide AQUÍ, en la frontera,
    // porque después `asEmptyString` lo convierte en `''` y nadie podría distinguir «no ha terminado»
    // de «no sé cuándo terminó». Quien pinte lee `open`, no interpreta una cadena vacía.
    open: change.endedAt === null || change.endedAt === undefined,
    status: asObject(change.status),
    reason: asObject(change.reason),
    // Los DOS operarios: pueden ser personas distintas —uno deja la máquina en un estado y otro la
    // saca—, y eso es información.
    openedBy: asObject(change.openedBy),
    closedBy: asObject(change.closedBy),
    deviceId: change.deviceId,
    deviceName: asEmptyString(change.deviceName),
    // `forced` llega SIEMPRE, también en `false`, porque «ausente» y «no forzado» no pueden
    // confundirse en el cable. Pintarlo solo cuando es cierto es una regla de PINTADO, no de este
    // mapeo — aquí se conserva tal cual.
    forced: change.forced === true
  })),
  // S432 — el ÚLTIMO REPORTE de la operación (S408), para el titular de Progress. Sun lo sirve
  // `null` cuando no hay ninguno, y entonces `at` queda en `''`: quien pinte pregunta por `at`.
  // Campo a campo y no el objeto entero: pasado entero, la guarda de contrato no vería qué se lee.
  lastReport: {
    at: asEmptyString((data.lastReport || {}).at),
    workerCode: asEmptyString((data.lastReport || {}).workerCode),
    workerName: asEmptyString((data.lastReport || {}).workerName)
  }
  // (`inspectionItems` —S411, las mismas filas que las mediciones de S437— lo retiró Sun el 23/09.)
})

// ═══ CALIDAD AL MODELO DE MOS (S429 Workflow · S437/S439 Progress) — forma DESPLEGADA por Sun el
// 23/09 (`4f6c45545d`) y capturada en vivo sobre LTT002 ═══
//
// La CARACTERÍSTICA es la misma en las dos puertas, y se lee igual que la pinta
// `Mos/Widgets/Inspection/InspectionReportDetail.jsx`: orden, código, nombre, TIPO LEGIBLE
// (`featureTypeName`), la PREGUNTA (`qualitativeAttribute`), el nominal (`value`), las tolerancias
// (solo en RANGE), unidad, frecuencia, herramientas y punto de inspección.
// ⚠️ `featureId` es un UUID, no un número (medido en vivo).
const mapCharacteristicDto = (characteristic) => ({
  featureId: characteristic.featureId,
  featureOrder: asEmptyString(characteristic.featureOrder),
  code: asEmptyString(characteristic.code),
  name: asEmptyString(characteristic.name),
  // `RANGE · TEXT · BOOLEAN · LIST · DATE · NO_RESPONSE`, tal cual: es la clave de cómo se pinta.
  dataType: asEmptyString(characteristic.dataType),
  featureTypeName: asEmptyString(characteristic.featureTypeName),
  qualitativeAttribute: asEmptyString(characteristic.qualitativeAttribute),
  value: asEmptyString(characteristic.value),
  upperTolerance: asEmptyString(characteristic.upperTolerance),
  lowerTolerance: asEmptyString(characteristic.lowerTolerance),
  unitCode: asEmptyString(characteristic.unitCode),
  frequency: asEmptyString(characteristic.frequency),
  toolReferences: (characteristic.toolReferences || []).map((tool) => ({
    code: asEmptyString(tool.code),
    name: asEmptyString(tool.name),
    serializable: tool.serializable === true
  })),
  inspectionPoint: {
    id: (characteristic.inspectionPoint || {}).id,
    name: asEmptyString((characteristic.inspectionPoint || {}).name)
  }
})

// ⚠️ `group: null` —una característica SIN GRUPO— se decide AQUÍ, antes de que `asObject` lo funda
// con un grupo real de campos vacíos: `hasGroup` lo dice, y quien pinte no lo infiere de tres huecos.
const mapFeatureGroupsDto = (featureGroups, mapCharacteristic) => (featureGroups || []).map((featureGroup) => ({
  hasGroup: !!featureGroup.group,
  group: {
    code: asEmptyString((featureGroup.group || {}).code),
    name: asEmptyString((featureGroup.group || {}).name),
    description: asEmptyString((featureGroup.group || {}).description)
  },
  characteristics: (featureGroup.characteristics || []).map(mapCharacteristic)
}))

// S427 / S428 / S429 — LO TEÓRICO de una operación: tareas con su tiempo, sus características en
// grupos, y los recursos teóricos por tipo. Se pide AL DESPLEGAR, una operación cada vez.
// Máximos medidos por operación: 17 tareas, 75 recursos, 118 características.
export const mapOperationTheoreticalDto = (data) => ({
  tasks: ((data || {}).tasks || []).map((task) => ({
    taskReferenceId: task.taskReferenceId,
    order: asEmptyString(task.order),
    name: asEmptyString(task.name),
    theoreticalDurationSeconds: asEmptyString(task.theoreticalDurationSeconds),
    featureGroups: mapFeatureGroupsDto(task.featureGroups, mapCharacteristicDto)
  })),
  resourceGroups: ((data || {}).resourceGroups || []).map((group) => ({
    type: asEmptyString(group.type),
    // ⚠️ `processResourceId` es un UUID; `name: null` es un recurso que el catálogo no resuelve.
    resources: (group.resources || []).map((resource) => ({
      processResourceId: resource.processResourceId,
      name: asEmptyString(resource.name),
      quantityFirstUnit: asEmptyString(resource.quantityFirstUnit),
      unitCode: asEmptyString(resource.unitCode)
    }))
  }))
})

export const fetchOperationTheoretical = (fabricationOrderId, jobReferenceId) =>
  apiGet(`fabrication-orders/${fabricationOrderId}/detail/operations/${jobReferenceId}/theoretical`).then(mapOperationTheoreticalDto)

// S437 / S439 — LA CALIDAD REAL: un bloque por INFORME de inspección (reinspecciones incluidas, cada una
// su bloque), con TODAS las características de su tarea y, en cada una, lo que se midió.
// ⚠️⚠️ SIN VEREDICTO en ninguna capa (PO, 23/09): ni `recordedResult` ni estado de tolerancia.
// ⚠️ `measurement: null` es una característica SIN MEDIR —la fila sale entera y dice «sin medir»—, y
// se decide aquí (`measured`) para que nadie lo infiera de tres campos vacíos.
// ⚠️ `worker` del informe viene `null` en el 100 % (medido en vivo): el único «quién midió» es
// `measurement.responsible`. Se mapea igual, por si algún día llega.
const mapMeasuredCharacteristicDto = (characteristic) => ({
  ...mapCharacteristicDto(characteristic),
  measured: !!characteristic.measurement,
  measurement: {
    value: asEmptyString((characteristic.measurement || {}).value),
    serialNumber: asEmptyString((characteristic.measurement || {}).serialNumber),
    responsible: asEmptyString((characteristic.measurement || {}).responsible)
  }
})

export const mapOperationQualityDto = (data) => ({
  inspectionReports: ((data || {}).inspectionReports || []).map((report) => ({
    inspectionReportId: report.inspectionReportId,
    partId: report.partId,
    reportDate: asEmptyString(report.reportDate),
    worker: {
      workerId: (report.worker || {}).workerId,
      code: asEmptyString((report.worker || {}).code),
      name: asEmptyString((report.worker || {}).name)
    },
    deviceId: report.deviceId,
    deviceName: asEmptyString(report.deviceName),
    task: {
      taskReferenceId: (report.task || {}).taskReferenceId,
      name: asEmptyString((report.task || {}).name)
    },
    featureGroups: mapFeatureGroupsDto(report.featureGroups, mapMeasuredCharacteristicDto)
  }))
})

// Se pide AL DESPLEGAR la operación en Progress, una vez por operación, igual que N4.
export const fetchOperationQuality = (fabricationOrderId, jobReferenceId) =>
  apiGet(`fabrication-orders/${fabricationOrderId}/detail/progress/operations/${jobReferenceId}/quality`).then(mapOperationQualityDto)

// N4 — one operation's dropdown, fetched on demand when the row is expanded (CA4.3): no other
// window opens for this.
export const fetchOperationExpansion = (fabricationOrderId, jobReferenceId) =>
  apiGet(`fabrication-orders/${fabricationOrderId}/detail/operations/${jobReferenceId}`).then(mapOperationExpansionDto)

// N5 — writes value AND provenance in the same transaction (RN38-RN41); empty string/null clears
// both (D18/R-3). This route is always human writing: the response's `planningGroupOrigin` always
// comes back MANUAL with `assignedBy` populated — OperationsBlock applies it as-is, it never
// re-derives the provenance.
export const savePlanningGroup = (fabricationOrderId, jobReferenceId, planningGroup) =>
  apiPut(
    `fabrication-orders/${fabricationOrderId}/operations/${jobReferenceId}/planning-group`,
    { planningGroup: planningGroup === '' ? null : planningGroup }
  )

// Miniatura de la imagen principal de la referencia de pieza, para la foto de la cabecera.
//
// ⚠ NO es `document/file/download/{uuid}` con un `<img src>`: eso daba 401 SIEMPRE, porque una
// petición de imagen disparada por el navegador no puede llevar la cabecera `Authorization` que sí
// lleva `apiDownloadFile`. Es el mismo endpoint en lote que el listado de OFs usa para sus filas
// (`useMainImageResolution`), pedido para una sola referencia: devuelve base64 JPEG SIN prefijo
// `data:`, indexado por identificador. Ausencia de imagen = cadena vacía, nunca un error.
// El `Promise.resolve()` de delante no es adorno: la foto es DECORACIÓN de la cabecera y nada de lo
// que le pase puede tumbarla. Así, un fallo síncrono de la frontera sale como promesa rechazada —que
// el llamante ya trata— en vez de como excepción durante el render.
export const fetchMainImageThumbnail = (partReferenceId) =>
  Promise.resolve()
    .then(() => apiPost('partReferences/mainImages/thumbnails', [partReferenceId]))
    .then((thumbnails) => asEmptyString(asObject(thumbnails)[partReferenceId]))

// N14 — pause from the detail's own route (idempotent, DETAIL audit origin, its own authority).
// Task 21: besides its outcome it hands back `ignoredParts`, the same list the listing's
// order/pause/{id} returns (FINISHED parts and parts on a multi-task job), so the detail runs the
// listing's follow-up dialogs on it. A missing list is an empty one.
export const pauseOrderParts = (id) =>
  apiPut(`fabrication-orders/${id}/pause`).then((data) => asObject(data).ignoredParts || [])

// N15 — resume from the detail's own route; `ignoredParts` = the FINISHED parts it left alone.
export const resumeOrderParts = (id) =>
  apiPut(`fabrication-orders/${id}/resume`).then((data) => asObject(data).ignoredParts || [])

// The listing's per-part pause for a part on a device: `endJob` true waits for the job to end.
export const pausePart = (partId, endJob) =>
  apiPut(`part/pause/${partId}/${endJob}`)

// X4 (C3/C4) — status change has NO unitary route: the detail reuses the bulk endpoint with a
// selection reduced to the single open order, the same RequestPagination body shape the listing
// table already sends (plan-jupiter.md §2/§9, S303).
//
// ⚠ CONTRASTADO EL 2026-09-15, contra el productor y contra el consumidor, después de que el PO
// probara el cambio de estatus y Sun devolviera 500 SIEMPRE, para cualquier orden y cualquier
// destino. Antes aquí había una nota diciendo que el contraste estaba "pendiente", y llevaba dos
// semanas puesta: el cuerpo se había construido a mano desde una forma SUPUESTA.
//
// `size: 0` era la causa. `PaginationBuilder.buildPagination` hace `PageRequest.of(page, size)`, que
// exige tamaño >= 1 y revienta con "Page size must not be less than one!" antes de mirar nada más.
// Va `-1` y no `1` por dos razones, las dos comprobadas y no deducidas:
//   · ese mismo constructor traduce `-1` a `Integer.MAX_VALUE`, que es "sin límite" — lo correcto
//     para una SELECCIÓN, que no es una página;
//   · y es el idioma que ya usa el listado cuando reduce una selección a mano
//     (`FabricationOrder.jsx:672`, la acción Borrar, en producción desde antes de esta historia).
// Además, `PostgresFabricationOrderRepository:227-229` construye la paginación pero llama a
// `findAll(specification)` SIN el pageable: el tamaño no puede ensanchar ni recortar lo afectado.
// Lo que restringe a nuestra orden es `rows` + `primaryKeys` + `allSelected: false`, vía
// `buildMultiSelection`.
//
// El resto de campos neutros SÍ están contrastados contra `usePagination.jsx` + el builder de Sun:
// con `genericSearch` vacío no se construye filtro genérico (las `columns` dan igual), sin
// `columnFilters` no hay filtro de columna, y `extraFilterName` ausente no genera especificación
// porque el builder exige que exista un campo declarado con ese nombre en la entidad.
const NEUTRAL_STATUS_CHANGE_REQUEST = {
  parameterFilters: [],
  parameterFiltersValues: [],
  parameterFiltersNames: [],
  page: 0,
  size: -1,
  columns: [],
  mixedColumnFilters: '{}',
  genericSearch: '',
  genericSearchExcludedColumns: [],
  excludedColumns: [],
  sortKey: [],
  sortMode: []
}

export const buildStatusChangeRequest = (fabricationOrderId, primaryKeys = ['id']) => ({
  ...NEUTRAL_STATUS_CHANGE_REQUEST,
  rows: [String(fabricationOrderId)],
  allSelected: false,
  primaryKeys
})

export const changeStatus = (fabricationOrderId, newStatus, primaryKeys) =>
  apiPost(
    `fabricationOrderMulti/status/${newStatus}?origin=DETAIL`,
    buildStatusChangeRequest(fabricationOrderId, primaryKeys)
  )

// FoDetailProgressDto (N10) -> business shape consumed by ProgressBlock (CU-L9, T4).
// H8/R-14: `summary` is literally the header's QuantityBreakdown (good/rejected/remaining ==
// pending) — this mapper never computes a second figure, it only applies the same null-avoidance
// as everywhere else ('' rather than 0, RN8/RN61).
/**
 * El motivo por el que un hito no se ha alcanzado, ya en la forma que pinta la pantalla.
 *
 * ⚠️⚠️ S420 — SUN DEJÓ DE MANDAR FRASES Y MANDA UN CÓDIGO. Antes llegaba `"Quedan 6 unidades por
 * fabricar"`, en castellano, y se pintaba igual en los ocho idiomas: un texto compuesto en el
 * servidor no pasa por los ficheros de traducción, así que el único sitio donde existe el idioma se
 * saltaba entero. Ahora llega `{ code, args }` y la frase se arma aquí.
 *
 * ⚠️ `args` llega SIEMPRE, vacío si no hay números (contrato de Sun, nunca `null`), así que quien
 * pinta lee un mapa y no tiene que distinguir una ausencia.
 *
 * 📌 `legacyText` EXISTE SOLO PARA UN JAR ANTERIOR A S420, y va con fecha: el backend que corre hoy
 * (construido a las 12:57, S420 se commiteó a las 13:54) todavía manda la frase. Mapear solo la
 * forma nueva rompería la pantalla del PO ahora mismo; mapear las dos y callarlo la dejaría en un
 * respaldo eterno. Se enseña verbatim porque es lo único que se puede hacer con una frase que ya
 * viene en un idioma decidido, y **esta rama se retira en cuanto el backend se reconstruya**.
 */
const mapPendingReasonDto = (pendingReason) => {
  if (pendingReason === null || pendingReason === undefined) return {}
  if (typeof pendingReason === 'string') return pendingReason === '' ? {} : { code: '', args: {}, legacyText: pendingReason }
  return { code: asEmptyString(pendingReason.code), args: asObject(pendingReason.args), legacyText: '' }
}

const mapMilestoneDto = (milestone) => ({
  kind: milestone.kind,
  state: milestone.state,
  occurredAt: asEmptyString(milestone.occurredAt),
  origin: asEmptyString(milestone.origin),
  actor: asObject(milestone.actor),
  pendingReason: mapPendingReasonDto(milestone.pendingReason),
  // ⚠️⚠️ S419 — ESTOS DOS SE QUEDARON FUERA Y LA PANTALLA LOS LEÍA IGUAL. `ProgressBlock` usa
  // `milestone.id` para la clave de cada bloque y `milestone.plannedStartAt` para la fecha a la que
  // se movió el trabajo; sin mapearlos llegaban `undefined` SIEMPRE, así que dos replanificaciones
  // compartían clave —la avería que S419 vino a quitar— y la fecha no se pintaba nunca.
  //
  // 📌 Y NINGÚN TEST LO VIO porque los de S419 inyectan el `progress` a mano, sin cruzar esta
  // frontera. Es exactamente el error contra el que yo mismo escribí un aviso en
  // `DetailHeader.absentFigures.test.jsx` unas horas antes: un test que monta el dato a mano prueba
  // un caso que la aplicación no produce nunca.
  plannedStartAt: asEmptyString(milestone.plannedStartAt),
  id: asEmptyString(milestone.id)
})

const mapOperationProgressDto = (operation) => ({
  jobReferenceId: operation.jobReferenceId,
  // ADDENDUM 19 (E25) parte B, S353 — CUARTA instancia del mismo defecto: N10 servía el CÓDIGO del
  // nodo bajo un campo llamado `name`. Sun ya lo separó (`code` + un `name` nuevo desde `node.name`),
  // y esta puerta seguía leyendo `name`.
  //
  // ⚠️ No es un cambio preventivo: estaba PASANDO. Con el jar del 15/09 16:35, la columna TRABAJO del
  // bloque de Progreso salía VACÍA en las siete filas de `ofref2p` —comprobado en pantalla—, porque
  // el `name` nuevo es nulo en 2.906 de 2.907 nodos. Sin un error, sin una excepción y sin una traza:
  // exactamente la forma que el addendum 17 describió para N3 y N1.
  code: asEmptyString(operation.code),
  name: asEmptyString(operation.name),
  done: asEmptyString(operation.done),
  // ADDENDUM 11 (E17): `pending` — lo que QUEDA por reportar, no un objetivo. El nombre anterior
  // (`expected`) invitaba a dividir uno entre otro, y la columna de avance salía invertida.
  pending: asEmptyString(operation.pending),
  // ⚠️ N10 también renombró este campo (ADDENDUM 13 / E19): es `actualDurationSeconds`, el tiempo
  // REAL de los pasos cerrados. El hub no lo mencionó para esta puerta, pero el DTO de Sun lo trae
  // así; seguir leyendo `declaredTimeSeconds` habría dejado el dato en blanco sin que nada fallara.
  actualDurationSeconds: asEmptyString(operation.actualDurationSeconds),
  // S431 — las rechazadas de la operación: la fila y el titular de Progress las leen de aquí. ⚠️ N10
  // no trae el DESTINO del rechazo; ese sigue viniendo de N4, en el titular.
  // ⚠️⚠️ Add. 64 I: el TEÓRICO de N10 NO se mapea. Sun lo sirve, pero la fila lo lee de N3 —la misma
  // cifra que Workflow, «una cifra, un origen»—, así que mapearlo era dejar una segunda fuente a mano
  // para que alguien la usara. Declarado en la guarda como servido y sin consumidor.
  rejectedQuantity: asEmptyString(operation.rejectedQuantity),
  // (`inspectionSummary` —el veredicto FAIL resumido— lo retiró Sun el 23/09 por decisión del PO.)
  openIncidenceCount: asEmptyString(operation.openIncidenceCount),
  // S421 — LOS MATERIALES QUE CONSUMIÓ ESTA OPERACIÓN. Array SIEMPRE presente, vacío si no consumió
  // nada: no hay que distinguir «no consumió» de «no llegó».
  //
  // ⚠️⚠️ AGREGADO POR TIPO DE MATERIAL, NO POR LOTE, y está medido: por lote quedan 348 filas en un
  // job y por tipo 45. Agregar por lote NO AGRUPA NADA —los 348 son 348 lotes distintos, cada uno
  // una vez— y ese 348 sale de UN paso sobre UNA pieza. El lote se pierde a sabiendas (12 de 1.527
  // consumos lo tienen informado); si algún día hace falta, es un desglose DETRÁS de la fila, nunca
  // en su lugar.
  //
  // ⚠️ `unit` es `string | null` y LA AUSENCIA SE DECLARA (RN8): un consumo sin unidad es «0,18 de
  // algo». Y ojo al dato que casi hace que no se sirviera: es null en 0 de 1.527 en la base del PO
  // y en 4 de 10 en `mic_testing` — mirando solo la primera se habría concluido que el campo no
  // existe. Ninguna base sola es una muestra.
  //
  // ⚠️ `consumption` viaja como NÚMERO, así que el JSON no transporta ceros finales: `0.180` llega
  // como `0.18`. El valor es exacto; la escala no. No se puede deducir la precisión del propio
  // número —`0.18` y `0.180` son el mismo número y distinta afirmación sobre la medida—, así que
  // quien pinte decide cuántos decimales y lo escribe.
  resources: (operation.resources || []).map((resource) => ({
    rawMaterialReferenceId: resource.rawMaterialReferenceId,
    code: asEmptyString(resource.code),
    name: asEmptyString(resource.name),
    consumption: asEmptyString(resource.consumption),
    unit: asEmptyString(resource.unit),
    // No se pinta: está para que quien dude de una suma vea de cuántos hechos sale.
    consumptionCount: asEmptyString(resource.consumptionCount)
  }))
})

export const mapProgressDto = (data) => ({
  summary: {
    good: asEmptyString(data.summary ? data.summary.good : null),
    rejected: asEmptyString(data.summary ? data.summary.rejected : null),
    remaining: asEmptyString(data.summary ? data.summary.remaining : null)
  },
  milestones: (data.milestones || []).map(mapMilestoneDto),
  operations: (data.operations || []).map(mapOperationProgressDto)
})

export const fetchProgress = (id) =>
  apiGet(`fabrication-orders/${id}/detail/progress`).then(mapProgressDto)

// FoDetailReportDto (N11, Page) -> business shape consumed by ProgressBlock's report list (CU-L10,
// T4). RN63.1/R-15/D27: this DTO has NO declared-time field — this mapper never adds one, not even
// as ''. RN8.2/H2: `quantities[]` travels exactly as registered, no grouping/sum/good-or-rejected
// label added here or anywhere downstream (R-14b).
export const mapReportDto = (report) => ({
  reportId: report.reportId,
  reportedAt: report.reportedAt,
  operation: asObject(report.operation),
  // ⚠️⚠️ CAMPO A CAMPO, y con el nombre QUE SIRVE SUN: `{ workerId, workerName }` (capturado en vivo el
  // 23/09). Pasado entero, la pantalla leía `worker.userName` —que N11 no ha servido nunca— y la
  // columna «Worker» salía «—» en TODOS los reportes de TODAS las órdenes (lo vio el PO). Las fixtures
  // de los tests traían `userName`, así que el defecto pasaba en verde, y la guarda de contrato no
  // cubría N11. Ahora la cubre, y por dentro de `worker`.
  worker: {
    workerId: (report.worker || {}).workerId,
    workerName: asEmptyString((report.worker || {}).workerName)
  },
  device: asObject(report.device),
  quantities: (report.quantities || []).map((quantity) => ({
    qualityTypeId: quantity.qualityTypeId,
    qualityTypeName: quantity.qualityTypeName,
    quantity: quantity.quantity
  })),
  comment: asEmptyString(report.comment),
  inspections: (report.inspections || []).map((inspection) => ({
    featureName: inspection.featureName,
    value: inspection.value,
    unit: asEmptyString(inspection.unit),
    result: inspection.result
  })),
  incidence: asObject(report.incidence),
  documents: report.documents || []
})

export const fetchReports = (id, page = 0, size = 20) =>
  apiGet(`fabrication-orders/${id}/detail/reports?page=${page}&size=${size}`).then((data) => ({
    content: ((data && data.content) || []).map(mapReportDto),
    totalElements: (data && data.totalElements) || 0,
    page: (data && data.number) || 0,
    size: (data && data.size) || size
  }))

// FoDetailDocumentsDto (N6) -> business shape consumed by DocumentsBlock (CU-L5/CU-W5, T5a).
// RN46/P-3: `sizeBytes` stays the raw frontier value (possibly `null`) on purpose, same exception as
// `deviation.days` above — DocumentsBlock's size label decides "—" from it, NEVER from a coerced `0`.
const mapDocumentDto = (doc) => ({
  documentId: doc.documentId,
  uuid: doc.uuid,
  name: doc.name,
  documentType: asEmptyString(doc.documentType),
  extension: asEmptyString(doc.extension),
  belongsTo: asObject(doc.belongsTo),
  createdUser: asEmptyString(doc.createdUser),
  createdDate: asEmptyString(doc.createdDate),
  sizeBytes: doc.sizeBytes === undefined ? null : doc.sizeBytes,
  // D10/RN45: `true` only for the EXECUTION group's own-order documents — the other three groups
  // always come back `false` (Sun-authoritative, never re-derived here).
  attachable: !!doc.attachable
})

const mapDocumentGroupDto = (group) => ({
  provenance: group.provenance,
  documents: (group.documents || []).map(mapDocumentDto)
})

// RN44/RN6: the 4 groups always come back, even empty — this mapper never drops or synthesizes one,
// it only applies the map above to whatever Sun sends.
export const mapDocumentsDto = (data) => ({
  groups: (data.groups || []).map(mapDocumentGroupDto)
})

export const fetchDocuments = (id) =>
  apiGet(`fabrication-orders/${id}/detail/documents`).then(mapDocumentsDto)

// N16 (addendum 9 / E15) — la puerta propia del bloque General:
// `PUT fabrication-orders/{id}/detail/general`, **cuerpo PARCIAL**.
//
// Invierte la invariante de X1: **"lo que no viaja, no se toca"**. La preservación de la fila deja de
// ser nuestra (ya no reconstruimos la fila desde X2) y pasa a ser del endpoint. Por eso este cuerpo
// NO hereda la invariante del addendum 6: mandarla entera "por si acaso" sería reintroducir ruido.
//
// Sun distingue **clave ausente** de **clave con `null`** leyendo el JSON crudo (`JsonNode.has()`,
// `FoDetailGeneralEditJsonMapper.java:73`): ausente = no tocar, presente con `null` = borrar. Esa
// distinción es responsabilidad del que construye el cuerpo (GeneralBlock), no de esta capa.
//
// `confirmedDeliveryDate` es parámetro de CONSULTA, no del cuerpo: es la bandera de reintento del 409
// de sugerencia de entrega (E3) y viaja tal cual.
export const saveGeneralDetail = (fabricationOrderId, body, confirmedDeliveryDate) => {
  const query = confirmedDeliveryDate ? `?confirmedDeliveryDate=${encodeURIComponent(confirmedDeliveryDate)}` : ''
  return apiPut(`fabrication-orders/${fabricationOrderId}/detail/general${query}`, body)
}

// ═══ H179 — EL WORKFLOW DE LA OF (C1 lectura · C2 asignar/cambiar/quitar) ═══
//
// C1 (`FoWorkflowAssignmentDto`, 13 campos): si el workflow se puede tocar, por qué no, y lo que el diálogo
// de confirmación necesita saber. Las DOS listas llegan siempre (vacías si no hay motivos) y los booleanos
// nunca nulos; lo que puede venir `null` es `workflow` (OF sin workflow), `workflowChangedBy/At` (nunca
// cambió tras el alta, G11) y `lastModified` (legal, HU 123). Aquí se decide cada ausencia; aguas abajo no
// hay `null`: `workflow` vacío es `{}` y se pregunta por `withoutWorkflow`, que es el dato del contrato.
//
// ⚠️ `workflow` es el workflow AL QUE APUNTA la OF: su COPIA `<code>_N` si existe. Sun lo sirve como
// `{ id, code, name, version }` con `version` DECIMAL (`1.0`, `contrato-sun.md` §7).
//
// H187 (C3-187): `workflow.origin` is `{ known, code, version }` when the FO points to a copy, and `null`/absent when
// it is not a linked copy. It maps to `{}` ("not a copy", no `null` downstream); with `known:false`, `code` and
// `version` arrive as `null` and become empty strings here.
const mapAssignmentOrigin = (origin) => (origin
  ? { known: !!origin.known, code: asEmptyString(origin.code), version: asEmptyString(origin.version) }
  : {})

const mapAssignmentWorkflow = (workflow) => (workflow
  ? { id: workflow.id, code: asEmptyString(workflow.code), name: asEmptyString(workflow.name), version: asEmptyString(workflow.version), origin: mapAssignmentOrigin(workflow.origin) }
  : {})

export const mapWorkflowAssignmentDto = (data) => ({
  fabricationOrderId: data.fabricationOrderId,
  workflow: mapAssignmentWorkflow(data.workflow),
  withoutWorkflow: !!data.withoutWorkflow,
  editable: !!data.editable,
  notEditableReasons: data.notEditableReasons || [],
  clearable: !!data.clearable,
  notClearableReasons: data.notClearableReasons || [],
  workflowHasJobDocuments: !!data.workflowHasJobDocuments,
  workflowHasCamPrograms: !!data.workflowHasCamPrograms,
  partReferenceId: data.partReferenceId,
  workflowChangedBy: asEmptyString(data.workflowChangedBy),
  workflowChangedAt: asEmptyString(data.workflowChangedAt),
  lastModified: asEmptyString(data.lastModified)
})

// C1 — `GET /endpoints/fabrication-orders/{id}/workflow-assignment`.
export const fetchWorkflowAssignment = (fabricationOrderId) =>
  apiGet(`fabrication-orders/${fabricationOrderId}/workflow-assignment`).then(mapWorkflowAssignmentDto)

// C2 — `PUT /endpoints/fabrication-orders/{id}/workflow`. ⚠️ LAS DOS CLAVES VAN SIEMPRE (EM-1):
// `workflowId: null` EXPLÍCITO es quitar el workflow, y una clave ausente es E12 —ya no se lee como
// quitar—. `expectedLastModified` es la marca que leyó la ficha (RN27); `null` es legal (semántica N16).
// La respuesta son los 13 de C1 (estado fresco) + `kind` (lo que de verdad pasó, puede ser NO_OP) +
// `generatedChildCount` (nunca nulo).
export const mapWorkflowAssignmentResultDto = (data) => ({
  ...mapWorkflowAssignmentDto(data),
  kind: asEmptyString(data.kind),
  generatedChildCount: Number(data.generatedChildCount) || 0
})

export const saveWorkflowAssignment = (fabricationOrderId, workflowId, expectedLastModified) =>
  apiPut(`fabrication-orders/${fabricationOrderId}/workflow`, {
    workflowId: workflowId === undefined || workflowId === '' ? null : workflowId,
    expectedLastModified: expectedLastModified === undefined || expectedLastModified === '' ? null : expectedLastModified
  }).then(mapWorkflowAssignmentResultDto)

// C10 — `GET /endpoints/workflows/approved/{partReferenceId}`, SIN CAMBIOS: el mismo que ya usa el alta. Ya
// aplica la admisibilidad de RN20 (V16); sirve `IdName` (`{ id, name }`, el nombre versionado).
export const fetchApprovedWorkflows = (partReferenceId) =>
  apiGet(`workflows/approved/${partReferenceId}`).then((data) =>
    (Array.isArray(data) ? data : []).map((workflow) => ({ id: workflow.id, name: asEmptyString(workflow.name) })))

// X-plan-types — catálogo de tipos de plan de fabricación. NO es un endpoint nuevo: es el mismo
// que ya consumía la columna `select` del listado (`FabricationOrder.jsx`), servido por
// `FabricationOrderPlanTypeRest.java:43` sobre la tabla `smartpm_cell.fabrication_order_plan_type`.
// Se mapea a `{ id, code }` para que el desplegable no dependa de campos que no usa.
export const fetchPlanTypes = () =>
  apiGet('getFabricationOrderPlanTypes').then((data) =>
    (data || []).map((planType) => ({ id: planType.id, code: planType.code })))

// X-clients — catálogo de clientes. NO es un endpoint nuevo: es el MISMO que servía el
// `smartSelect` de la columna «Client code» de la ventana antigua (`FabricationOrder.jsx:532`),
// que es justo la edición que hay que devolver. Se mapea a `{ id, code, name }` porque el
// desplegable necesita el ID —N16 vincula el cliente POR ID, no por código— y enseña el nombre.
export const fetchClients = () =>
  apiGet('clientsBasicData').then((data) =>
    (data || []).map((client) => ({ id: client.id, code: client.code, name: client.name })))

// Parent FO candidates: the SAME source the legacy row's smartSelect uses (`FabricationOrder.jsx`,
// column `parentFo.fabricationOrder`). With an id Sun excludes only that order itself, not its descendants.
export const fetchParentFoOptions = (fabricationOrderId, search) =>
  apiGet('allFabricationOrderSimpleData' + (fabricationOrderId ? '/' + fabricationOrderId : ''), {
    columnFilters: ['fabricationOrder'],
    columnFilterValues: [search],
    rows: [],
    page: 0,
    size: 10,
    allSelected: false
  }).then((data) => ((data && data.content) || []).map((fo) => ({ id: fo.id, fabricationOrder: fo.fabricationOrder })))

// FoDetailPartDto (N12, Page) -> business shape consumed by PartsBlock's grid (RN66, T5b).
// `result` already comes as 'OK'|'NOK'|'NONE' (no null-avoidance needed, the contract has no null
// case for it) and `lifecycleGroup` is computed IN SUN (RN66/PartLifecycleGroupPolicy) — this
// mapper never re-derives it from `status`.
const mapPartDto = (part) => ({
  partId: part.partId,
  code: part.code,
  status: part.status,
  result: part.result,
  lifecycleGroup: part.lifecycleGroup,
  lastUpdatedAt: asEmptyString(part.lastUpdatedAt)
})

// N12 — page, search (by code) and lifecycleGroup filter are ALWAYS forwarded to the server
// (RN70/D20, P-7): this function never fetches more than one page to filter in the browser.
export const fetchParts = (fabricationOrderId, { page = 0, size = 20, search = '', lifecycleGroup = '', excludeCancelled = false } = {}) => {
  const query = [`page=${page}`, `size=${size}`]
  if (search) query.push(`search=${encodeURIComponent(search)}`)
  if (lifecycleGroup) query.push(`lifecycleGroup=${lifecycleGroup}`)
  // The instance grid asks for the order WITHOUT its cancelled parts: they have their own section
  // and their own request, and mixed in they used up page slots the grid then hid.
  if (excludeCancelled) query.push('excludeCancelled=true')
  return apiGet(`fabrication-orders/${fabricationOrderId}/detail/parts?${query.join('&')}`).then((data) => ({
    content: ((data && data.content) || []).map(mapPartDto),
    totalElements: (data && data.totalElements) || 0,
    page: (data && data.number) || 0,
    size: (data && data.size) || size
  }))
}

// Parts counts: the order composition in one call — the total and the five lifecycle groups. The
// answer is checked, not trusted: a reply that is not that shape (an older Sun answering something
// else) throws, and the caller falls back to the per-group probes.
export const mapPartCountsDto = (data) => {
  const byGroup = data && data.byGroup
  const isCount = (value) => Number.isInteger(value) && value >= 0
  if (!data || !isCount(data.total) || !byGroup || !PART_LIFECYCLE_GROUPS.every((group) => isCount(byGroup[group]))) {
    throw new Error('Unexpected parts counts answer')
  }
  return { total: data.total, byGroup: Object.fromEntries(PART_LIFECYCLE_GROUPS.map((group) => [group, byGroup[group]])) }
}

export const fetchPartCounts = (fabricationOrderId) =>
  apiGet(`fabrication-orders/${fabricationOrderId}/detail/parts/counts`).then(mapPartCountsDto)

// A 404 means two things: Sun answers "the order does not exist" with a message body, while an older
// Sun that has no counts route answers the framework 404 (`path`/`timestamp`, no Sun message). Only the
// first one is the order being gone; any other failure of the counts call falls back to the probes.
export const isOrderNotFoundError = (error) => {
  const body = error && error.data
  return !!error && error.status === 404 && !!body && typeof body === 'object' &&
    typeof body.message === 'string' && body.message !== '' && !('path' in body) && !('timestamp' in body)
}

// PartTrackDto (N13) -> business shape consumed by PartsBlock's part sheet + operation track
// (RN67/RN68/RN69, T5b). RN67: this DTO has NO `revision` field for the reference — this mapper
// never adds one, not even as ''.
//
// ⚠️ S392/S393 — AQUÍ HABÍA UNA PROHIBICIÓN Y LA HAN LEVANTADO, pero solo a medias. Este comentario
// decía que el `worker` del paso se tiraba a propósito para que nadie pudiera leerlo. Sun RETIRÓ
// ese campo (addendum 38) y publica `operationWorkers` en su lugar, así que la prohibición de
// PUBLICAR se levantó — la RAZÓN de ser de aquel campo-cerrojo, NO:
//
//   SIGUE PROHIBIDO rellenar esto desde el operario del parte de cantidad (N10). Aquel dato es por
//   OF+operación, no por pieza, y ponerlo aquí sería trazabilidad falsa. Es exactamente el mismo
//   error que el cerrojo impedía; lo único que ha cambiado es que ahora existe un campo legítimo
//   al lado, y por eso el cerrojo ya no hace falta.
//
// 📌 Y EL NOMBRE ES EL CONTRATO (R-HANDOFF-2), no un detalle de estilo: `operationWorkers`, nunca
// `worker` ni `workers`. Dentro del paso de UNA PIEZA, cualquiera de los dos cortos se lee como
// "el operario de esta pieza", que es un dato que NO EXISTE en el modelo. El nombre largo es lo
// único que queda separando esta lista de esa lectura, ahora que la forma ya no lo impide.
//
// Sun garantiza `workerId` presente (un registro sin nadie logueado no aporta operario, no aporta
// un operario anónimo) y lista vacía en vez de `null`. Código y nombre son `Optional` SUELTOS:
// puede venir uno sin el otro, y por eso se normalizan por separado.
const mapPartTrackStepDto = (step) => ({
  jobReferenceId: step.jobReferenceId,
  jobOrder: step.jobOrder,
  name: step.name,
  state: step.state,
  startedAt: asEmptyString(step.startedAt),
  finishedAt: asEmptyString(step.finishedAt),
  device: asObject(step.device),
  batch: asEmptyString(step.batch),
  result: asEmptyString(step.result),
  resultDescription: asEmptyString(step.resultDescription),
  incidence: asObject(step.incidence),
  operationWorkers: (step.operationWorkers || []).map(mapOperationWorkerDto)
})

export const mapPartTrackDto = (data) => ({
  part: {
    id: data.part.id,
    code: data.part.code,
    partReference: asObject(data.part.partReference),
    fabricationOrder: asObject(data.part.fabricationOrder),
    batch: asEmptyString(data.part.batch),
    mountedComponents: data.part.mountedComponents || [],
    lastUpdatedAt: asEmptyString(data.part.lastUpdatedAt),
    lastResult: asEmptyString(data.part.lastResult)
  },
  steps: (data.steps || []).map(mapPartTrackStepDto)
})

export const fetchPartTrack = (partId) =>
  apiGet(`parts/${partId}/detail/track`).then(mapPartTrackDto)

// FoDetailChildDto (N7) -> business shape consumed by MaterialsBlock's Necesidades/dependencies
// (CU-L6, T6). `originJobReference`/`risk.consumingOperationName` stay objects/'' rather than null
// (frontier rule); `originProvenance`/`risk.status` are always-present enums, never mapped.
const mapChildDto = (child) => ({
  fabricationOrderId: child.fabricationOrderId,
  code: child.code,
  partReference: asObject(child.partReference),
  quantity: asEmptyString(child.quantity),
  status: child.status,
  // C1/R-20: statusProgress is text, progressPercent is the number that actually paints this
  // column (RN48/S306) — this mapper keeps both, distinct, never converts one into the other.
  statusProgress: asEmptyString(child.statusProgress),
  progressPercent: asEmptyString(child.progressPercent),
  deliveryDate: asEmptyString(child.deliveryDate),
  plannedEndDate: asEmptyString(child.plannedEndDate),
  originJobReference: asObject(child.originJobReference),
  originProvenance: child.originProvenance,
  risk: {
    status: child.risk ? child.risk.status : 'UNKNOWN',
    daysLate: asEmptyString(child.risk ? child.risk.daysLate : null),
    consumingOperationName: asEmptyString(child.risk ? child.risk.consumingOperationName : null)
  }
})

// N7 — direct children only (RN48); Sun returns `[]` for an order with no children (S258), this
// mapper never adds a placeholder row. Defensive against a non-array response (e.g. an unmocked
// route in a test), same convention as N8 below.
export const fetchChildren = (id) =>
  apiGet(`fabrication-orders/${id}/children`).then((data) => (Array.isArray(data) ? data : []).map(mapChildDto))

// FoDetailMaterialRequirementDto (N8) -> business shape consumed by MaterialsBlock's Necesidades/
// requirements (CU-L7, T6). RN54.0/R-17/H9: requiredSecondUnit/secondUnitLabel stay the raw
// frontier value (possibly null) on purpose — they are informational-only and NEVER enter
// missingQuantity/coverage, downstream code must decide "nothing extra to show" from null itself,
// not from a coerced ''. D12(b): sharedStock is a plain boolean, a contract fact, never re-derived.
const mapMaterialRequirementDto = (row) => ({
  resource: asObject(row.resource),
  operation: asObject(row.operation),
  unit: asEmptyString(row.unit),
  unitQuantity: asEmptyString(row.unitQuantity),
  requiredQuantity: asEmptyString(row.requiredQuantity),
  mountedQuantity: asEmptyString(row.mountedQuantity),
  availableQuantity: row.availableQuantity === undefined ? null : row.availableQuantity,
  missingQuantity: asEmptyString(row.missingQuantity),
  requiredSecondUnit: row.requiredSecondUnit === undefined ? null : row.requiredSecondUnit,
  secondUnitLabel: row.secondUnitLabel === undefined ? null : row.secondUnitLabel,
  sharedStock: !!row.sharedStock,
  coverage: row.coverage,
  coveringFabricationOrder: asObject(row.coveringFabricationOrder)
})

// N8 — excludes skipped operations already server-side (RN53.1/D29); Sun returns `[]` for an
// order without BOM (S268/RN6), this mapper never invents a row. Defensive against a non-array
// response (e.g. an unmocked route in a test).
export const fetchMaterialRequirements = (id) =>
  apiGet(`fabrication-orders/${id}/detail/material-requirements`).then((data) => (Array.isArray(data) ? data : []).map(mapMaterialRequirementDto))

// FoDetailConsumedMaterialDto (N9, Page) -> business shape consumed by MaterialsBlock's Asignados
// (CU-L8, T6). RN57: this is always REAL consumption, there is no "reserved" state to map.
const mapConsumedMaterialDto = (row) => ({
  rawMaterialId: row.rawMaterialId,
  resource: asObject(row.resource),
  batch: asEmptyString(row.batch),
  source: asObject(row.source),
  operation: asObject(row.operation),
  part: asObject(row.part),
  quantity: asEmptyString(row.quantity),
  consumedAt: asEmptyString(row.consumedAt)
})

// N9 — paginated (P-7), same page shape convention as N11/N12 (content/totalElements/number/size).
export const fetchConsumedMaterial = (id, page = 0, size = 20) =>
  apiGet(`fabrication-orders/${id}/detail/material-consumed?page=${page}&size=${size}`).then((data) => ({
    content: ((data && data.content) || []).map(mapConsumedMaterialDto),
    totalElements: (data && data.totalElements) || 0,
    page: (data && data.number) || 0,
    size: (data && data.size) || size
  }))
